import React, { useMemo, useState } from 'react';

import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { FlexResetPeriod } from '@enums/account/resetPeriods';
import flexControls from '@messages/flexControls/flexControls';
import flexControlsValidations from '@messages/flexControls/flexControlsValidations';
import getEnumTranslation from '@utils/getEnumTranslation';

interface ResetPeriodSharedProps {
    isEditing?: boolean;
}

const ResetPeriodForm = ({ isEditing }: ResetPeriodSharedProps) => {
    const {
        control,
        getValues,
        formState: { errors },
    } = useFormContext();
    const { theme } = useTheme();
    const formValues = getValues();
    const [duration, setDuration] = useState(formValues?.limit_duration);
    const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ];

    const { formatMessage } = useIntl();
    const limitDurationAriaLabel = useMemo(
        () =>
            formatMessage({
                id: 'ResetPeriod.limitDurationAriaLabel',
                defaultMessage: 'Limit duration buttons',
            }),
        [formatMessage],
    );

    return (
        <Grid container spacing={3} flexDirection="row">
            <Grid item xs={12}>
                {isEditing && (
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            color:
                                theme?.custom?.brand?.textMain ||
                                'text.primary',
                            mb: 2,
                        }}
                        data-testid="basic-details-shared-edit-title">
                        {getEnumTranslation('reset_period', {
                            messages: flexControls,
                        })}
                    </Typography>
                )}
                <Controller
                    name="limit_duration"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FormControl>
                            <RadioGroup
                                value={value}
                                row
                                aria-label={limitDurationAriaLabel}
                                name="control-type-radio-buttons"
                                onChange={(event, value) => {
                                    setDuration(value);
                                    onChange(value);
                                }}>
                                <FormControlLabel
                                    data-testid="monthly-radio-button"
                                    value="monthly"
                                    control={
                                        <Radio data-testid="monthly-type-radio" />
                                    }
                                    label={getEnumTranslation('monthly', {
                                        messages: flexControls,
                                    })}
                                    sx={{ ml: 1, p: 0.5 }}
                                />
                                <FormControlLabel
                                    data-testid="weekly-radio-button"
                                    value="weekly"
                                    control={
                                        <Radio data-testid="weekly-type-radio" />
                                    }
                                    label={getEnumTranslation('weekly', {
                                        messages: flexControls,
                                    })}
                                    sx={{ ml: 1, p: 0.5 }}
                                />
                                <FormControlLabel
                                    data-testid="daily-radio-button"
                                    value="daily"
                                    control={
                                        <Radio data-testid="daily-type-radio" />
                                    }
                                    label={getEnumTranslation('daily', {
                                        messages: flexControls,
                                    })}
                                    sx={{ ml: 1, p: 0.5 }}
                                />
                            </RadioGroup>
                            {!!errors.limit_duration && (
                                <FormHelperText>
                                    <Box
                                        component="span"
                                        sx={{
                                            color: theme?.palette?.error?.main,
                                        }}>
                                        {getEnumTranslation(
                                            errors?.limit_duration?.message.toString(),
                                            {
                                                messages:
                                                    flexControlsValidations,
                                            },
                                        )}
                                    </Box>
                                </FormHelperText>
                            )}
                        </FormControl>
                    )}
                />
            </Grid>
            {!!duration && (
                <>
                    {duration === FlexResetPeriod.WEEKLY && (
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="week_day"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <TextField
                                        data-testid="week-day"
                                        select
                                        onChange={onChange}
                                        value={value}
                                        fullWidth
                                        label={
                                            <FormattedMessage
                                                id="ResetPeriod.weekDay"
                                                defaultMessage="Week day"
                                            />
                                        }
                                        error={!!errors.week_day}
                                        helperText={
                                            !!errors.week_day && (
                                                <Box component="span">
                                                    {getEnumTranslation(
                                                        errors?.week_day?.message.toString(),
                                                        {
                                                            messages:
                                                                flexControlsValidations,
                                                        },
                                                    )}
                                                </Box>
                                            )
                                        }>
                                        {days.map((day: string) => {
                                            return (
                                                <MenuItem key={day} value={day}>
                                                    {getEnumTranslation(day, {
                                                        messages: flexControls,
                                                    })}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                )}
                            />
                        </Grid>
                    )}
                    {duration === FlexResetPeriod.MONTHLY && (
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="month_day"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        value={value}
                                        onChange={onChange}
                                        type="number"
                                        data-testid="month-day"
                                        label={
                                            <FormattedMessage
                                                id="BasicDetails.monthDay"
                                                defaultMessage="Month day"
                                            />
                                        }
                                        fullWidth
                                        inputProps={{ min: 1, max: 28 }}
                                        error={!!errors.month_day}
                                        helperText={
                                            !!errors.month_day && (
                                                <Box component="span">
                                                    {getEnumTranslation(
                                                        errors?.month_day?.message.toString(),
                                                        {
                                                            messages:
                                                                flexControlsValidations,
                                                        },
                                                    )}
                                                </Box>
                                            )
                                        }
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="time"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="time"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    data-testid="time"
                                    label={
                                        <FormattedMessage
                                            id="Conditions.time"
                                            defaultMessage="Time"
                                        />
                                    }
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default ResetPeriodForm;
